import { Component, Host, Prop, h } from "@stencil/core";
import type {
  BreadcrumbItemModel,
  ItemLink,
  GetImagePathCallback
} from "../types";

@Component({
  tag: "gx-ide-breadcrumb-item",
  styleUrl: "breadcrumb-item.scss",
  shadow: true
})
export class GxIdeBreadcrumbItem {
  /**
   * Model of the item (used for image resolution and data).
   */
  @Prop() readonly model!: BreadcrumbItemModel;

  /**
   * Visible text. Falls back to model.caption when not set.
   */
  @Prop() readonly caption?: string;

  /**
   * aria-label for the item. Falls back to accessibleName or caption when not set.
   */
  @Prop() readonly accessibleName?: string;

  /**
   * Whether the item is disabled.
   */
  @Prop() readonly disabled?: boolean;

  /**
   * When set, the item renders as an anchor; otherwise as a button.
   */
  @Prop() readonly link?: ItemLink;

  /**
   * Whether this item is the current page (selected).
   */
  @Prop() readonly selected: boolean = false;

  /**
   * Whether to show visual indicator when selected is true.
   */
  @Prop() readonly selectedLinkIndicator: boolean = false;

  /**
   * Image source (URL or CSS url()).
   */
  @Prop() readonly startImgSrc?: string;

  /**
   * How to render the start image: "background" or "mask".
   */
  @Prop() readonly startImgType?: "background" | "mask";

  /**
   * Optional callback to resolve image path for this item.
   */
  @Prop() readonly getImagePathCallback?: GetImagePathCallback;

  /**
   * Item id used by the container for click delegation (data-item-id).
   */
  @Prop() readonly itemId!: string;

  #resolveImageSrc = (): string | undefined => {
    if (this.getImagePathCallback) {
      const result = this.getImagePathCallback(this.model);
      if (result?.base) return result.base;
    }
    return this.startImgSrc ?? this.model?.startImgSrc;
  };

  #getCaption = (): string => this.caption ?? this.model?.caption ?? "";

  #getAccessibleName = (): string =>
    this.accessibleName ??
    this.model?.accessibleName ??
    this.#getCaption() ??
    "";

  #renderStartImage = (): ReturnType<typeof h> | string | undefined => {
    const src = this.#resolveImageSrc();
    if (!src) return undefined;

    const type = this.startImgType ?? this.model?.startImgType;
    const isUrl = src.trim().toLowerCase().startsWith("url(");

    if (isUrl && (type === "background" || type === "mask")) {
      return (
        <span
          class={{
            "item__img": true,
            "item__img--background": type === "background",
            "item__img--mask": type === "mask"
          }}
          style={
            type === "background"
              ? { backgroundImage: src }
              : { maskImage: src, WebkitMaskImage: src }
          }
          aria-hidden="true"
        />
      );
    }

    return (
      <ch-image
        class="item__img item__img-icon icon-m"
        src={src}
        aria-hidden="true"
      />
    );
  };

  render() {
    const caption = this.#getCaption();
    const ariaLabel = this.#getAccessibleName();
    const isDisabled = this.disabled ?? this.model?.disabled ?? false;
    const hasLink = this.link?.url !== undefined && this.link?.url !== "";
    const imgContent = this.#renderStartImage();

    const captionClass = {
      "caption": true,
      "body-regular-s": !this.selected || !this.selectedLinkIndicator,
      "body-semi-bold-s": this.selected && this.selectedLinkIndicator
    };
    const content = (
      <span class="item-content">
        {imgContent}
        {caption ? (
          <span class={captionClass} part="item-caption">
            {caption}
          </span>
        ) : null}
      </span>
    );

    const actionClass = {
      "action": true,
      "disabled": isDisabled,
      "selected": this.selected && this.selectedLinkIndicator,
      "not-selected": !this.selected
    };

    if (hasLink && !isDisabled) {
      return (
        <Host
          role="listitem"
          data-item-id={this.itemId}
          data-has-link="true"
          part="item-action item-link"
        >
          <a
            class={{ ...actionClass, "link-primary": true }}
            href={this.link!.url}
            aria-current={this.selected ? "page" : undefined}
            aria-label={ariaLabel || undefined}
            part="item-link"
          >
            {content}
          </a>
        </Host>
      );
    }

    if (hasLink && isDisabled) {
      return (
        <Host
          role="listitem"
          data-item-id={this.itemId}
          data-has-link="true"
          part="item-action disabled"
        >
          <span
            class={actionClass}
            aria-disabled="true"
            aria-label={ariaLabel || undefined}
            part="item-link disabled"
          >
            {content}
          </span>
        </Host>
      );
    }

    return (
      <Host
        role="listitem"
        data-item-id={this.itemId}
        data-has-link="false"
        part="item-action item-button"
      >
        <button
          type="button"
          class={actionClass}
          disabled={isDisabled}
          aria-label={ariaLabel || undefined}
          part="item-button"
        >
          {content}
        </button>
      </Host>
    );
  }
}
